import { FC, useState } from 'react'
import classNames from 'classnames'
import { TrendBadge } from 'src/components/TrendBadge'
import { useAdPerfCreatives } from 'src/reactQuery/useAdPerformance'
import { AdPerfAd, AdPerfRange } from 'src/services/adPerformanceApi'
import s from './dashboard.module.scss'
import {
  BoltIcon,
  CarouselIcon,
  ChartBoxIcon,
  GridIcon,
  ImageIcon,
  PlayIcon,
  WarnIcon,
} from './icons'
import { compact, money, pct } from '../format'

const FORMATS = [
  { key: '', label: 'All formats' },
  { key: 'video', label: 'Video' },
  { key: 'image', label: 'Image' },
  { key: 'carousel', label: 'Carousel' },
]

// Rule-based advice until AI recommendations land (plan phase 3).
const ADVICE: Record<string, { action: string; text: string }> = {
  needs_tweak: {
    action: 'Tweak',
    text: 'Middle of the pack. Try a stronger first frame or a clearer call to action.',
  },
  top_performer: {
    action: 'Scale up',
    text: 'Among your best ads of this format. Consider moving budget here.',
  },
  underperforming: {
    action: 'Pause',
    text: 'Low CTR and high cost vs similar ads. Pause or redesign.',
  },
}
const LABEL_TEXT: Record<string, string> = {
  needs_tweak: 'Needs tweak',
  top_performer: 'Top performer',
  underperforming: 'Underperforming',
}
const tone = (label: AdPerfAd['label']) =>
  label === 'top_performer' ? 'Top' : label === 'needs_tweak' ? 'Mid' : 'Low'
const rateColor = (label: AdPerfAd['label']) =>
  label === 'top_performer'
    ? '#4caf50'
    : label === 'needs_tweak'
      ? '#d4894b'
      : '#ef4444'

const formatTag = (a: AdPerfAd) =>
  a.format === 'carousel'
    ? `Carousel · ${a.cards} cards`
    : a.format === 'video'
      ? 'Video'
      : 'Image'

interface CardProps {
  ad: AdPerfAd
  currency: string
  onOpen: (adId: string) => void
}

const CreativeCard: FC<CardProps> = ({ ad, currency, onOpen }) => {
  const advice = ad.fatigued
    ? {
        action: 'Refresh',
        text: 'Fatigued: people see it often and CTR is dropping. Refresh the creative.',
      }
    : ad.label
      ? ADVICE[ad.label]
      : null
  const rates = [
    { label: 'Hook rate', value: ad.format === 'video' ? ad.hook_rate : null },
    { label: 'Engagement', value: ad.engagement_rate },
    { label: 'Conv. rate', value: ad.conversion_rate },
  ]
  return (
    <button
      className={s.creative}
      onClick={() => onOpen(ad.ad_id)}
      type="button"
    >
      <div
        className={s.thumb}
        style={
          ad.thumbnail_url
            ? { backgroundImage: `url(${ad.thumbnail_url})` }
            : undefined
        }
      >
        <span className={s.thumbTag}>
          {ad.format === 'video' ? (
            <PlayIcon />
          ) : ad.format === 'carousel' ? (
            <CarouselIcon />
          ) : (
            <ImageIcon />
          )}
          {formatTag(ad)}
        </span>
        {ad.score !== null && (
          <span className={classNames(s.score, s[`score${tone(ad.label)}`])}>
            Score {ad.score}
          </span>
        )}
      </div>
      <div className={s.creativeBody}>
        <div className={s.creativeName} title={ad.name}>
          {ad.name}
        </div>
        <div className={s.miniStats}>
          <div>
            {pct(ad.ctr, 1)}
            <small>CTR</small>
          </div>
          <div>
            {money(ad.cpc, currency, 2)}
            <small>CPC</small>
          </div>
          <div>
            {compact(ad.impressions)}
            <small>Imp.</small>
          </div>
          <div>
            {compact(ad.clicks)}
            <small>Clicks</small>
          </div>
          <div>
            {money(ad.spend, currency)}
            <small>Spend</small>
          </div>
        </div>
        {rates.map((r) => (
          <div className={s.rateRow} key={r.label}>
            <span>{r.label}</span>
            <div className={s.rateTrack}>
              <span
                style={{
                  background: rateColor(ad.label),
                  width: `${Math.min(100, r.value ?? 0)}%`,
                }}
              />
            </div>
            <span>{r.value === null ? '–' : pct(r.value, 1)}</span>
          </div>
        ))}
        {advice && <p className={s.advice}>{advice.text}</p>}
        <div className={s.labels}>
          {ad.label && (
            <span className={classNames(s.label, s[`label${tone(ad.label)}`])}>
              {LABEL_TEXT[ad.label]}
            </span>
          )}
          {advice && (
            <span className={classNames(s.label, s.labelAction)}>
              {advice.action}
            </span>
          )}
          {!ad.label && (
            <span className={s.small}>Not enough delivery to score</span>
          )}
        </div>
      </div>
    </button>
  )
}

interface Props {
  currency: string
  onOpenAd: (adId: string) => void
  range: AdPerfRange
}

export const Creatives: FC<Props> = ({ currency, onOpenAd, range }) => {
  const [format, setFormat] = useState('')
  const [showAll, setShowAll] = useState(false)
  const { data, isLoading } = useAdPerfCreatives(range, format || undefined)
  const ads = data?.ads ?? []
  const sum = data?.summary
  const visible = showAll ? ads : ads.slice(0, 12)

  return (
    <div className={s.card}>
      <div className={s.cardHeader}>
        <h3 className={s.cardTitle}>Creative Assets Analysis</h3>
        <div className={s.formatTabs}>
          {FORMATS.map((f) => (
            <button
              className={classNames(s.formatTab, {
                [s.formatTabActive]: format === f.key,
              })}
              key={f.key}
              onClick={() => setFormat(f.key)}
              type="button"
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className={s.kpis}>
        <div className={s.kpi}>
          <div className={s.kpiTitle}>
            <GridIcon /> Total Creatives
          </div>
          <div className={s.kpiBody}>
            <span className={s.kpiValue}>{sum?.total_creatives ?? '–'}</span>
            <span className={s.kpiSide}>
              <TrendBadge value={sum?.total_creatives_change} />
              {sum?.total_creatives_change != null && (
                <small>vs previous period</small>
              )}
            </span>
          </div>
        </div>
        <div className={s.kpi}>
          <div className={s.kpiTitle}>
            <ChartBoxIcon /> Top Performer CTR
          </div>
          <div className={s.kpiBody}>
            <span className={s.kpiValue}>{pct(sum?.top_ctr ?? null, 1)}</span>
            {sum?.top_ctr_vs_avg && (
              <span className={s.kpiSide}>
                <span className={s.shareBadge}>{sum.top_ctr_vs_avg}x</span>
                <small>avg. CTR</small>
              </span>
            )}
          </div>
        </div>
        <div className={s.kpi}>
          <div className={s.kpiTitle}>
            <BoltIcon /> Avg. Hook Rate
          </div>
          <div className={s.kpiBody}>
            <span className={s.kpiValue}>
              {pct(sum?.avg_hook_rate ?? null, 1)}
            </span>
            <span className={s.kpiSide}>
              <small>3-sec views ÷ impressions</small>
            </span>
          </div>
        </div>
        <div className={s.kpi}>
          <div className={s.kpiTitle}>
            <WarnIcon /> Fatigued Assets
          </div>
          <div className={s.kpiBody}>
            <span className={s.kpiValue}>
              {sum?.fatigued ?? '–'} {sum?.fatigued === 1 ? 'Asset' : 'Assets'}
            </span>
            {!!sum?.fatigued && (
              <span className={s.kpiSide}>
                <small className={s.danger}>Needs refresh</small>
              </span>
            )}
          </div>
        </div>
      </div>
      <h4 className={s.subTitle}>Creatives</h4>
      {!isLoading && !ads.length && (
        <div className={s.empty}>No ads delivered in this period.</div>
      )}
      <div className={classNames(s.creatives, { [s.creativesAll]: showAll })}>
        {visible.map((ad) => (
          <CreativeCard
            ad={ad}
            currency={currency}
            key={ad.ad_id}
            onOpen={onOpenAd}
          />
        ))}
      </div>
      {ads.length > 4 && (
        <div className={s.center}>
          <button
            className={s.primaryButton}
            onClick={() => setShowAll((v) => !v)}
            type="button"
          >
            {showAll ? 'View Less' : `View All Creatives (${ads.length})`}
          </button>
        </div>
      )}
    </div>
  )
}
